import { createContext, useState, useEffect } from 'react';
import type { FC, ReactNode } from 'react';
import type { UserInfo } from '../types';
import { authService } from '@services/authService';
import { employeeService } from '@services/employeeService';
import toast from 'react-hot-toast';

interface AuthContextType {
  user: UserInfo | null;
  userInfo: UserInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  signup: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmPasswordReset: (email: string, code: string, newPassword: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      // Check if user has valid session
      const session = await authService.getCurrentSession();
      const idToken = session.getIdToken().getJwtToken();
      const userData = authService.getUserDataFromToken(idToken);

      // Fetch employee info
      const employeeInfo = await employeeService.getByEmail(userData.email);
      setUser(employeeInfo);
    } catch (error) {
      // No valid session
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      await authService.login(email, password);

      // Get user info from API
      const employeeInfo = await employeeService.getByEmail(email);
      setUser(employeeInfo);

      toast.success('Login successful!');
    } catch (error: any) {
      const message = error.message || 'Login failed';
      toast.error(message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      authService.logout();
      setUser(null);
      toast.success('Logged out successfully');
    } catch (error: any) {
      toast.error('Logout failed');
      throw error;
    }
  };

  const signup = async (email: string, password: string, firstName: string, lastName: string) => {
    try {
      setIsLoading(true);
      await authService.signup(email, password, firstName, lastName);
      toast.success('Signup successful! Please check your email for verification.');
    } catch (error: any) {
      const message = error.message || 'Signup failed';
      toast.error(message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      setIsLoading(true);
      await authService.forgotPassword(email);
      toast.success('Verification code sent to your email!');
    } catch (error: any) {
      const message = error.message || 'Failed to send reset code';
      toast.error(message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const confirmPasswordReset = async (email: string, code: string, newPassword: string) => {
    try {
      setIsLoading(true);
      await authService.confirmPasswordReset(email, code, newPassword);
      toast.success('Password reset successfully! Please login with your new password.');
    } catch (error: any) {
      const message = error.message || 'Password reset failed';
      toast.error(message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    userInfo: user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    signup,
    forgotPassword,
    confirmPasswordReset,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
